/**
 * OTPKit — Professional OTP Input Library
 * @version 2.1.1
 * @license MIT
 *
 * iOS SMS Autofill - Production Ready Implementation
 * Fixed: iOS Chrome autofill detection
 * Tested on: iOS Safari 14+, iOS Chrome 90+, iOS Firefox
 */

const DEFAULTS = {
    length: 6,
    type: "numeric",
    customPattern: null,
    autoFocus: true,
    autoSubmit: false,
    placeholder: "",
    inputClass: "otpkit-input",
    containerClass: "otpkit-container",
    disabled: false,
    theme: "default",
    size: "responsive",
    gap: "medium",
    animation: true,
    hapticFeedback: true,
    maskInput: false,
    grouping: null,
    separator: "-",
    rtl: "auto",
    mobileOptimized: true,
    virtualKeyboardMode: "numeric",
    allowPasteAnywhere: true,
    clearOnError: false,
    selectOnFocus: true,
    moveOnDelete: true,
    submitOnComplete: false,
    validateOnType: true,
    errorAnimation: "shake",
    successAnimation: "scale",

    // iOS Autofill Configuration - Optimized for Chrome
    iosAutofillTimeout: 5000,
    iosAutofillPollInterval: 50, // Faster polling for Chrome
    iosAutofillDebounce: 0, // Immediate processing for Chrome

    onComplete: null,
    onChange: null,
    onError: null,
    onPaste: null,
    onKeyDown: null,
    onAutofill: null,
};

const utils = {
    isNumeric: (ch) => /^\d$/.test(ch),
    isAlphanumeric: (ch) => /^[A-Za-z0-9]$/.test(ch),
    isRTL: () => {
        const html = document.documentElement;
        return (
            html.dir === "rtl" ||
            html.lang?.startsWith("ar") ||
            html.lang?.startsWith("he") ||
            html.lang?.startsWith("fa") ||
            html.lang?.startsWith("ur")
        );
    },
    isMobile: () => {
        return (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            ) ||
            "ontouchstart" in window ||
            navigator.maxTouchPoints > 0
        );
    },
    isIOS: () => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
    isIOSChrome: () => {
        return utils.isIOS() && /CriOS/i.test(navigator.userAgent);
    },
    isIOSSafari: () => {
        return (
            utils.isIOS() &&
            !/CriOS|FxiOS|OPiOS|mercury/i.test(navigator.userAgent)
        );
    },
    vibrate: (pattern = 10) => {
        if ("vibrate" in navigator) {
            navigator.vibrate(pattern);
        }
    },
    debounce: (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    },
    sanitizeOTP: (text) => {
        // Remove all non-alphanumeric characters
        return String(text).replace(/[^a-zA-Z0-9]/g, "");
    },
};

export default class OTPKit {
    constructor(container, options = {}) {
        if (!container) {
            throw new Error(
                "OTPKit: Container element or selector is required"
            );
        }

        this.opts = { ...DEFAULTS, ...options };
        this.container =
            typeof container === "string"
                ? document.querySelector(container)
                : container;

        if (!this.container) {
            throw new Error("OTPKit: Container element not found");
        }

        // Core properties
        this.length = Number(this.opts.length) || DEFAULTS.length;
        this.type = this.opts.type;
        this.inputs = [];
        this._value = new Array(this.length).fill("");
        this.disabled = Boolean(this.opts.disabled);
        this.events = new Map();

        // State management
        this._state = {
            userTyped: false,
            isComplete: false,
            isProcessing: false,
            lastProcessedValue: null,
        };

        // iOS Autofill state management
        this._autofill = {
            detected: false,
            timer: null,
            pollInterval: null,
            observer: null,
            listeners: [],
            lastCheckedValue: "",
        };

        // Detect RTL
        if (this.opts.rtl === "auto") {
            this.opts.rtl = utils.isRTL();
        }

        // Mobile optimizations
        if (this.opts.mobileOptimized && utils.isMobile()) {
            this.opts.size =
                this.opts.size === "medium" ? "responsive" : this.opts.size;
            this.opts.hapticFeedback = this.opts.hapticFeedback !== false;
        }

        this._init();
    }

    _init() {
        this._render();
        this._setupEventListeners();

        // iOS-specific initialization
        if (utils.isIOS()) {
            this._initializeIOSAutofill();
        }

        requestAnimationFrame(() => {
            this.container.classList.add("otpkit-ready");
        });

        if (this.opts.autoFocus) {
            setTimeout(() => this.focus(0), 100);
        }

        this._emit("ready", { instance: this });
    }

    _render() {
        this.container.innerHTML = "";
        this.inputs = [];

        const containerClasses = [
            this.opts.containerClass,
            `size-${this.opts.size}`,
            `gap-${this.opts.gap}`,
            `theme-${this.opts.theme}`,
        ];

        if (this.opts.rtl) {
            containerClasses.push("rtl");
            this.container.setAttribute("dir", "rtl");
        }

        this.container.className = containerClasses.join(" ");

        let inputIndex = 0;
        const groups = this.opts.grouping || [this.length];

        groups.forEach((groupSize, groupIndex) => {
            for (let i = 0; i < groupSize && inputIndex < this.length; i++) {
                this._createInput(inputIndex);
                inputIndex++;
            }

            if (groupIndex < groups.length - 1 && inputIndex < this.length) {
                const separator = document.createElement("span");
                separator.className = "otpkit-separator";
                separator.textContent = this.opts.separator;
                this.container.appendChild(separator);
            }
        });
    }

    _createInput(index) {
        const input = document.createElement("input");

        // Basic attributes
        input.type = "text";
        input.className = this.opts.inputClass;
        input.dataset.index = index;
        input.placeholder = this.opts.placeholder;

        // Critical: OTP autocomplete setup for first input only
        if (index === 0) {
            input.autocomplete = "one-time-code";
            input.name = "one-time-code";
            input.id = `otpkit-${this._generateUID()}`;
            // Allow full OTP length for autofill, will be handled programmatically
            input.maxLength = this.length;
        } else {
            input.autocomplete = "off";
            // Other inputs keep maxLength = 1
            input.maxLength = 1;
        }

        // Prevent autocorrection/capitalization
        input.setAttribute("autocorrect", "off");
        input.setAttribute("autocapitalize", "off");
        input.setAttribute("spellcheck", "false");

        // Input mode for virtual keyboard
        if (this.opts.type === "numeric") {
            input.inputMode = "numeric";
            input.pattern = "\\d*";
        } else if (this.opts.type === "alphanumeric") {
            input.inputMode = "text";
            input.pattern = "[A-Za-z0-9]*";
        } else {
            input.inputMode = this.opts.virtualKeyboardMode;
        }

        // Accessibility
        input.setAttribute(
            "aria-label",
            `OTP digit ${index + 1} of ${this.length}`
        );
        input.setAttribute("aria-required", "true");
        input.setAttribute("aria-invalid", "false");
        input.setAttribute("role", "textbox");

        if (this.disabled) {
            input.disabled = true;
        }

        if (this.opts.maskInput) {
            input.classList.add("masked");
        }

        this.container.appendChild(input);
        this.inputs.push(input);
    }

    _setupEventListeners() {
        this.inputs.forEach((input, index) => {
            // Paste event
            input.addEventListener(
                "paste",
                this._handlePaste.bind(this, index)
            );

            // Input event
            input.addEventListener(
                "input",
                this._handleInput.bind(this, index)
            );

            // Keydown event
            input.addEventListener(
                "keydown",
                this._handleKeydown.bind(this, index)
            );

            // Focus event
            input.addEventListener(
                "focus",
                this._handleFocus.bind(this, index)
            );

            // Mobile touch feedback
            if (utils.isMobile() && this.opts.hapticFeedback) {
                input.addEventListener("touchstart", () => utils.vibrate(5), {
                    passive: true,
                });
            }
        });

        // Container click
        this.container.addEventListener("click", (e) => {
            if (e.target === this.container) {
                const firstEmpty = this._getFirstEmptyIndex();
                this.focus(firstEmpty >= 0 ? firstEmpty : 0);
            }
        });
    }

    /**
     * iOS Autofill Detection System
     * Production-ready implementation for Safari and Chrome
     * FIXED: Chrome autofill now works reliably
     */
    _initializeIOSAutofill() {
        const firstInput = this.inputs[0];
        if (!firstInput) return;

        // Strategy 1: Direct input monitoring (critical for Chrome)
        this._setupDirectInputMonitoring(firstInput);

        // Strategy 2: Aggressive polling (works for both)
        this._startAggressivePolling(firstInput);

        // Strategy 3: Safari-specific detection
        if (utils.isIOSSafari()) {
            this._setupSafariAutofillDetection(firstInput);
            this._setupAutofillObserver(firstInput);
        }

        // Cleanup timeout
        this._autofill.timer = setTimeout(() => {
            this._cleanupAutofillDetection();
        }, this.opts.iosAutofillTimeout);
    }

    /**
     * Direct input monitoring - FIXED for iOS Chrome
     * This is the primary detection method for Chrome
     */
    _setupDirectInputMonitoring(firstInput) {
        const handler = (e) => {
            const value = e.target.value;

            // Multi-character input = autofill (Chrome behavior)
            if (value && value.length > 1) {
                // Prevent double processing
                if (this._autofill.detected || this._state.isProcessing) {
                    return;
                }

                e.preventDefault();
                e.stopImmediatePropagation();

                this._processAutofillValue(value);
            }
        };

        // Use capture phase to intercept early
        firstInput.addEventListener("input", handler, { capture: true });
        this._autofill.listeners.push({
            element: firstInput,
            event: "input",
            handler,
            capture: true,
        });
    }

    /**
     * Aggressive polling - FIXED with faster intervals
     * Essential for iOS Chrome where events may not fire
     */
    _startAggressivePolling(firstInput) {
        let checkCount = 0;
        const maxChecks = Math.ceil(
            this.opts.iosAutofillTimeout / this.opts.iosAutofillPollInterval
        );

        const checkAutofill = () => {
            if (this._autofill.detected || this._state.isProcessing) {
                if (this._autofill.pollInterval) {
                    clearInterval(this._autofill.pollInterval);
                    this._autofill.pollInterval = null;
                }
                return;
            }

            const currentValue = firstInput.value;

            // Chrome: value appears suddenly with no proper events
            if (
                currentValue &&
                currentValue.length > 1 &&
                currentValue !== this._autofill.lastCheckedValue
            ) {
                this._autofill.lastCheckedValue = currentValue;
                this._processAutofillValue(currentValue);
                return;
            }

            this._autofill.lastCheckedValue = currentValue;
            checkCount++;

            if (checkCount >= maxChecks) {
                if (this._autofill.pollInterval) {
                    clearInterval(this._autofill.pollInterval);
                    this._autofill.pollInterval = null;
                }
            }
        };

        // Immediate first check
        checkAutofill();

        // Start polling
        this._autofill.pollInterval = setInterval(
            checkAutofill,
            this.opts.iosAutofillPollInterval
        );
    }

    /**
     * Safari-specific autofill detection
     */
    _setupAutofillObserver(input) {
        if (typeof MutationObserver === "undefined") return;

        this._autofill.observer = new MutationObserver((mutations) => {
            if (this._autofill.detected || this._state.isProcessing) return;

            for (const mutation of mutations) {
                const value = input.value;
                if (value && value.length > 1) {
                    this._processAutofillValue(value);
                    break;
                }
            }
        });

        this._autofill.observer.observe(input, {
            attributes: true,
            attributeFilter: ["value"],
            characterData: true,
            childList: true,
            subtree: false,
        });
    }

    _setupSafariAutofillDetection(input) {
        // Safari-specific: animationstart event for autofill
        const keyframes = `
            @keyframes otpkit-autofill-detect {
                from { opacity: 0.99; }
                to { opacity: 1; }
            }
        `;

        // Inject keyframes if not exists
        if (!document.getElementById("otpkit-autofill-keyframes")) {
            const style = document.createElement("style");
            style.id = "otpkit-autofill-keyframes";
            style.textContent = keyframes;
            document.head.appendChild(style);
        }

        // Add animation to detect autofill
        const style = document.createElement("style");
        style.textContent = `
            #${input.id}:-webkit-autofill {
                animation: otpkit-autofill-detect 0.001s;
            }
        `;
        document.head.appendChild(style);

        const handler = () => {
            const value = input.value;
            if (value && !this._autofill.detected) {
                this._processAutofillValue(value);
            }
        };

        input.addEventListener("animationstart", handler);
        this._autofill.listeners.push({
            element: input,
            event: "animationstart",
            handler,
        });
    }

    /**
     * Process autofilled value - FIXED with immediate processing
     */
    _processAutofillValue(rawValue) {
        // Prevent duplicate processing
        if (this._autofill.detected || this._state.isProcessing) {
            return;
        }

        // Mark as processing immediately
        this._autofill.detected = true;
        this._state.isProcessing = true;

        // Clean the value
        const cleanValue = utils.sanitizeOTP(rawValue);

        if (!cleanValue) {
            this._state.isProcessing = false;
            this._autofill.detected = false;
            return;
        }

        // Save for deduplication
        this._state.lastProcessedValue = cleanValue;

        // Chrome needs immediate processing, Safari can have slight delay
        const delay = utils.isIOSChrome()
            ? 0
            : this.opts.iosAutofillDebounce || 0;

        setTimeout(() => {
            // Clear first input explicitly (Chrome needs this)
            this.inputs[0].value = "";

            // Clear all inputs first
            this.inputs.forEach((input) => {
                input.value = "";
            });

            // Fill the inputs
            this._fillInputsWithValue(cleanValue);

            // Update state
            this._state.isProcessing = false;

            // Emit events
            this._emit("autofill", {
                value: this.getValue(),
                source: utils.isIOSChrome() ? "ios-chrome" : "ios-safari",
            });

            this._emit("change", {
                value: this.getValue(),
                complete: this.isComplete(),
            });

            // Check completion
            if (this.isComplete()) {
                this._handleComplete();
            }

            // Cleanup detection
            this._cleanupAutofillDetection();

            // Haptic feedback
            if (this.opts.hapticFeedback) {
                utils.vibrate([10, 50, 10]);
            }
        }, delay);
    }

    _fillInputsWithValue(value) {
        const chars = value.split("");

        for (let i = 0; i < this.length && i < chars.length; i++) {
            const char = chars[i];
            if (this._validateChar(char)) {
                this._value[i] = char;
                this.inputs[i].value = char;
            }
        }

        // Focus last filled input
        const lastFilledIndex = Math.min(chars.length, this.length) - 1;
        if (lastFilledIndex >= 0) {
            setTimeout(() => {
                this.inputs[lastFilledIndex].focus();
            }, 50);
        }
    }

    _cleanupAutofillDetection() {
        // Clear timer
        if (this._autofill.timer) {
            clearTimeout(this._autofill.timer);
            this._autofill.timer = null;
        }

        // Clear polling
        if (this._autofill.pollInterval) {
            clearInterval(this._autofill.pollInterval);
            this._autofill.pollInterval = null;
        }

        // Disconnect observer
        if (this._autofill.observer) {
            this._autofill.observer.disconnect();
            this._autofill.observer = null;
        }

        // Remove event listeners
        this._autofill.listeners.forEach(
            ({ element, event, handler, capture }) => {
                element.removeEventListener(event, handler, capture);
            }
        );
        this._autofill.listeners = [];
    }

    /**
     * Standard event handlers
     */
    _handlePaste(index, e) {
        e.preventDefault();
        e.stopPropagation();

        const text = (e.clipboardData || window.clipboardData).getData("text");
        if (!text) return;

        const cleaned = utils.sanitizeOTP(text);
        if (!cleaned) return;

        // Custom handler
        if (this.opts.onPaste) {
            const result = this.opts.onPaste(cleaned, index);
            if (result === false) return;
        }

        this._fillInputsFromPaste(cleaned, index);
    }

    _fillInputsFromPaste(value, startIndex) {
        // Clear all first
        this.clear();

        const chars = value.split("");
        let currentIndex = 0;

        for (let i = 0; i < chars.length && currentIndex < this.length; i++) {
            const char = chars[i];
            if (this._validateChar(char)) {
                this._value[currentIndex] = char;
                this.inputs[currentIndex].value = char;
                currentIndex++;
            }
        }

        const focusIndex = Math.min(currentIndex, this.length - 1);
        setTimeout(() => {
            this.inputs[focusIndex].focus();
            this._checkComplete();
        }, 10);

        this._emit("paste", { value, applied: this.getValue() });
        this._emit("change", {
            value: this.getValue(),
            complete: this.isComplete(),
        });

        if (this.opts.hapticFeedback && utils.isMobile()) {
            utils.vibrate(10);
        }
    }

    /**
     * Handle input - FIXED to properly detect user typing vs autofill
     */
    _handleInput(index, e) {
        const value = e.target.value;

        // Multi-character input
        if (value.length > 1) {
            // On iOS first input, let autofill handler process if not detected yet
            if (utils.isIOS() && index === 0 && !this._autofill.detected) {
                // Process as paste fallback if autofill didn't catch it
                this._fillInputsFromPaste(value, index);
                this._state.userTyped = true;
                return;
            }

            // Regular paste or other multi-char input
            this._fillInputsFromPaste(value, index);
            this._state.userTyped = true;
            return;
        }

        // Single character input - definitely user typed
        if (value) {
            this._state.userTyped = true; // Mark BEFORE validation

            if (this._validateChar(value)) {
                this._value[index] = value;

                if (index < this.length - 1) {
                    this.inputs[index + 1].focus();
                } else {
                    this._checkComplete();
                }

                this._emit("change", { value: this.getValue(), index });
            } else {
                e.target.value = "";
            }
        } else {
            // Empty input
            this._value[index] = "";
            this._emit("change", { value: this.getValue(), index });
        }
    }

    _handleKeydown(index, e) {
        const key = e.key;

        if (key === "Backspace") {
            if (!this.inputs[index].value && index > 0) {
                e.preventDefault();
                this._value[index - 1] = "";
                this.inputs[index - 1].value = "";
                this.inputs[index - 1].focus();
                this._state.userTyped = true;
                this._emit("change", {
                    value: this.getValue(),
                    index: index - 1,
                });
            }
        } else if (key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            this.inputs[index - 1].focus();
        } else if (key === "ArrowRight" && index < this.length - 1) {
            e.preventDefault();
            this.inputs[index + 1].focus();
        } else if (key === "ArrowUp" || key === "ArrowDown") {
            e.preventDefault();
        }

        if (this.opts.onKeyDown) {
            this.opts.onKeyDown(e, index);
        }
    }

    _handleFocus(index, e) {
        if (this.opts.selectOnFocus && e.target.value) {
            setTimeout(() => e.target.select(), 0);
        }
        this._emit("focus", { index });
    }

    _validateChar(char) {
        if (!char) return false;

        switch (this.opts.type) {
            case "numeric":
                return utils.isNumeric(char);
            case "alphanumeric":
                return utils.isAlphanumeric(char);
            case "custom":
                return this.opts.customPattern
                    ? this.opts.customPattern.test(char)
                    : true;
            default:
                return true;
        }
    }

    _checkComplete() {
        if (this.isComplete() && !this._state.isComplete) {
            this._handleComplete();
        }
    }

    _handleComplete() {
        this._state.isComplete = true;
        const value = this.getValue();

        if (this.opts.successAnimation !== "none") {
            this.inputs.forEach((input) => {
                input.classList.add("success");
                setTimeout(() => input.classList.remove("success"), 500);
            });
        }

        if (this.opts.hapticFeedback && utils.isMobile()) {
            utils.vibrate([10, 50, 10]);
        }

        if (this.opts.onComplete) {
            this.opts.onComplete(value);
        }

        this._emit("complete", { value });

        if (this.opts.autoSubmit) {
            const form = this.container.closest("form");
            if (form) {
                setTimeout(() => {
                    form.dispatchEvent(
                        new Event("submit", {
                            bubbles: true,
                            cancelable: true,
                        })
                    );
                }, 100);
            }
        }
    }

    _getFirstEmptyIndex() {
        for (let i = 0; i < this._value.length; i++) {
            if (!this._value[i]) return i;
        }
        return -1;
    }

    _generateUID() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    _emit(event, data) {
        const callbackName = `on${event.charAt(0).toUpperCase()}${event.slice(
            1
        )}`;

        if (
            this.opts[callbackName] &&
            typeof this.opts[callbackName] === "function"
        ) {
            try {
                if (event === "complete" && data.value !== undefined) {
                    this.opts[callbackName](data.value);
                } else {
                    this.opts[callbackName](data);
                }
            } catch (err) {
                console.error(
                    `OTPKit: Error in ${callbackName} callback:`,
                    err
                );
            }
        }

        if (this.events.has(event)) {
            this.events.get(event).forEach((handler) => {
                try {
                    handler(data);
                } catch (err) {
                    console.error(`OTPKit: Error in ${event} handler:`, err);
                }
            });
        }

        const customEvent = new CustomEvent(`otpkit:${event}`, {
            detail: data,
            bubbles: true,
            cancelable: true,
        });
        this.container.dispatchEvent(customEvent);
    }

    // Public API Methods
    getValue() {
        return this._value.join("");
    }

    setValue(value) {
        const str = String(value || "");
        const cleaned = utils.sanitizeOTP(str);
        this._fillInputsWithValue(cleaned);
    }

    clear() {
        this._value = new Array(this.length).fill("");
        this.inputs.forEach((input) => {
            input.value = "";
            input.classList.remove("error", "success");
        });
        this._state.isComplete = false;
        this._state.userTyped = false;
        this._state.lastProcessedValue = null;
        this._autofill.lastCheckedValue = "";
        this._emit("clear");
    }

    focus(index = 0) {
        const i = Math.max(0, Math.min(this.length - 1, index));
        const input = this.inputs[i];
        if (input && !input.disabled) {
            input.focus();
        }
    }

    disable(flag = true) {
        this.disabled = Boolean(flag);
        this.inputs.forEach((input) => {
            input.disabled = this.disabled;
        });

        if (this.disabled) {
            this.container.classList.add("disabled");
        } else {
            this.container.classList.remove("disabled");
        }

        this._emit("disabled", { disabled: this.disabled });
    }

    enable() {
        this.disable(false);
    }

    isComplete() {
        return this._value.every((v) => v !== "");
    }

    isValid() {
        return this._value.every((v) => v === "" || this._validateChar(v));
    }

    getValues() {
        return [...this._value];
    }

    setTheme(themeName) {
        const validThemes = ["default", "minimal", "bordered", "underlined"];

        if (!validThemes.includes(themeName)) {
            console.warn(`OTPKit: Invalid theme "${themeName}"`);
            return;
        }

        this.container.className = this.container.className.replace(
            /theme-\w+/,
            `theme-${themeName}`
        );
        this.opts.theme = themeName;
    }

    setSize(size) {
        const validSizes = ["small", "medium", "large", "responsive"];

        if (!validSizes.includes(size)) {
            console.warn(`OTPKit: Invalid size "${size}"`);
            return;
        }

        this.container.className = this.container.className.replace(
            /size-\w+/,
            `size-${size}`
        );
        this.opts.size = size;
    }

    on(event, handler) {
        if (typeof handler !== "function") {
            throw new Error("OTPKit: Event handler must be a function");
        }

        if (!this.events.has(event)) {
            this.events.set(event, []);
        }

        this.events.get(event).push(handler);
        return () => this.off(event, handler);
    }

    off(event, handler) {
        if (!this.events.has(event)) return;

        if (!handler) {
            this.events.delete(event);
        } else {
            const handlers = this.events.get(event);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    once(event, handler) {
        const wrapper = (data) => {
            this.off(event, wrapper);
            handler(data);
        };
        return this.on(event, wrapper);
    }

    reset() {
        this.clear();
        this.focus(0);
        this._state.isComplete = false;
        this._state.userTyped = false;
        this._state.isProcessing = false;
        this._state.lastProcessedValue = null;
        this._autofill.detected = false;
        this._autofill.lastCheckedValue = "";
    }

    destroy() {
        // Cleanup autofill detection
        this._cleanupAutofillDetection();

        // Remove all event listeners
        this.inputs.forEach((input) => {
            const newInput = input.cloneNode(true);
            input.parentNode?.replaceChild(newInput, input);
        });

        // Clear DOM
        this.container.innerHTML = "";

        // Clear references
        this.inputs = [];
        this._value = [];
        this.events.clear();

        this._emit("destroy");
    }

    // Static methods
    static version = "2.1.1";

    static create(container, options) {
        return new OTPKit(container, options);
    }

    static createMultiple(selector, options) {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).map((el) => new OTPKit(el, options));
    }
}

// Auto-init
if (typeof document !== "undefined") {
    document.addEventListener("DOMContentLoaded", () => {
        const autoElements = document.querySelectorAll("[data-otpkit-auto]");
        autoElements.forEach((el) => {
            const options = el.dataset.otpkitOptions
                ? JSON.parse(el.dataset.otpkitOptions)
                : {};
            new OTPKit(el, options);
        });
    });
}

// Export
if (typeof module !== "undefined" && module.exports) {
    module.exports = OTPKit;
}
